import { post, pre } from "@typegoose/typegoose";

type MixedDoc = {
    $locals: Record<string, unknown>;
    isNew?: boolean;
    markModified: (path: string) => void;
    [key: string]: any;
};

function snapshotKey(fieldName: string): string {
    return `_original_${fieldName}`;
}

function takeSnapshot(doc: MixedDoc, fieldName: string): void {
    if (doc[fieldName] !== undefined) {
        doc.$locals[snapshotKey(fieldName)] = JSON.stringify(doc[fieldName]);
    }
}

function getSnapshot(doc: MixedDoc, fieldName: string): string | undefined {
    const val = doc.$locals[snapshotKey(fieldName)];
    return typeof val === "string" ? val : undefined;
}

// eslint-disable-next-line @typescript-eslint/ban-types
function applyHooks(constructor: Function, fieldName: string): void {
    // Capture the initial value on hydrated documents so pre-save can diff against it.
    const postInit = function (this: MixedDoc): void {
        takeSnapshot(this, fieldName);
    };

    // Query middleware receives one doc for findOne-like ops or an array for find().
    const postFind = function (this: unknown, doc: MixedDoc | MixedDoc[]): void {
        const docs = Array.isArray(doc) ? doc : [doc];
        for (const d of docs) {
            if (d?.$locals) takeSnapshot(d, fieldName);
        }
    };

    // Mixed fields mutate in place, so we compare snapshots and mark the path manually.
    const preSave = function (this: MixedDoc): void {
        if (this.isNew) {
            if (this[fieldName] !== undefined) this.markModified(fieldName);
            return;
        }
        const original = getSnapshot(this, fieldName);
        if (original !== undefined && JSON.stringify(this[fieldName]) !== original) {
            this.markModified(fieldName);
        }
    };

    const postSave = function (this: MixedDoc): void {
        const wasLoadedFromDb = getSnapshot(this, fieldName) !== undefined;
        const isExplicitlySet = fieldName in this && this[fieldName] !== undefined;
        if (wasLoadedFromDb || isExplicitlySet) {
            this.$locals[snapshotKey(fieldName)] = JSON.stringify(this[fieldName]);
        }
    };

    // A property decorator can register class-level middleware by invoking Typegoose's
    // `post`/`pre` decorator factories against the class constructor at runtime.
    post("init", postInit)(constructor);
    post(["find", "findOne", "findOneAndUpdate"], postFind)(constructor);
    pre("save", preSave)(constructor);
    post("save", postSave)(constructor);
}

export function trackMixed(target: object, propertyKey: string | symbol): void {
    applyHooks((target as any).constructor, String(propertyKey));
}
